import {
  defaultConvoyConfig,
  runConvoySim,
  type ConvoyConfig,
  type ConvoyResult,
  type ConvoySummary,
  type ConvoyTargeting,
  type ConvoyTopCover,
} from "@air-defence-sim/sim-core";
import { rangeControl, renderHeading, selectControl } from "../controls.js";
import { downloadBlob } from "../export.js";
import type { ThemeName } from "../theme.js";
import { buildCounterfactual, buildRunAnalysis } from "./analysis.js";
import { copyText, encodeConfigQuery, makeTracker, parseConfigQuery } from "../track.js";
import { CONVOY_PRESETS } from "./presets.js";
import { drawDroneSprite, makeConvoyRenderer } from "./render.js";
import { getConvoyPalette } from "./theme.js";

const canvas = document.getElementById("canvas") as HTMLCanvasElement;
const overlay = document.getElementById("overlay") as HTMLElement;
const controlsEl = document.getElementById("controls") as HTMLElement;
const scorecardEl = document.getElementById("scorecard") as HTMLElement;
const toolbarEl = document.getElementById("canvas-toolbar") as HTMLElement;
const analysisEl = document.getElementById("analysis") as HTMLElement;
const cmpEl = document.getElementById("cmp-figure") as HTMLElement;

const renderer = makeConvoyRenderer(canvas);
const tracker = makeTracker("convoy");

const SPEED_OPTIONS = [1, 2, 4, 8];
const DEFAULT_SPEED = 4;
const DEFAULT_THEME: ThemeName = "light";

const state: ConvoyConfig = defaultConvoyConfig();
let result: ConvoyResult | null = null;
let counterfactual: ConvoySummary | null = null;
let playing = false;
let simT = 0;
let lastFrame = 0;
let playbackSpeed = DEFAULT_SPEED;
let theme: ThemeName = DEFAULT_THEME;
let exportInProgress = false;

/* ---------- controls column ---------- */

controlsEl.innerHTML = "";
const setterByKey = new Map<string, (v: never) => void>();
let isApplyingPreset = false;

const presetSection = document.createElement("div");
presetSection.className = "preset-section";
presetSection.appendChild(renderHeading("Scenario preset"));
const presetSelect = document.createElement("select");
presetSelect.className = "preset-select";
for (const p of CONVOY_PRESETS) {
  const opt = document.createElement("option");
  opt.value = p.id;
  opt.textContent = p.label;
  presetSelect.appendChild(opt);
}
const customOpt = document.createElement("option");
customOpt.value = "__custom__";
customOpt.textContent = "— Custom —";
customOpt.disabled = true;
presetSelect.appendChild(customOpt);
presetSection.appendChild(presetSelect);
const presetDesc = document.createElement("p");
presetDesc.className = "preset-desc";
presetSection.appendChild(presetDesc);
controlsEl.appendChild(presetSection);

function markCustom(): void {
  if (isApplyingPreset) return;
  presetSelect.value = "__custom__";
  presetDesc.textContent = "Custom configuration.";
}

function trackedRange<K extends keyof ConvoyConfig>(
  label: string,
  key: K,
  min: number,
  max: number,
  step: number,
  format: (v: number) => string = (v) => `${v}`,
  cast: (v: number) => ConvoyConfig[K] = (v) => v as ConvoyConfig[K],
): void {
  const r = rangeControl(label, state[key] as number, min, max, step, (v) => {
    state[key] = cast(v);
    markCustom();
  }, format);
  setterByKey.set(key, r.set as (v: never) => void);
  controlsEl.appendChild(r.element);
}

function trackedSelect<T extends string, K extends keyof ConvoyConfig>(
  label: string,
  key: K,
  options: Array<[T, string]>,
  cast: (v: T) => ConvoyConfig[K] = (v) => v as unknown as ConvoyConfig[K],
): void {
  const s = selectControl<T>(label, state[key] as unknown as T, options, (v) => {
    state[key] = cast(v);
    markCustom();
  });
  setterByKey.set(key, ((v: ConvoyConfig[K]) => s.set(`${v}` as T)) as (v: never) => void);
  controlsEl.appendChild(s.element);
}

controlsEl.appendChild(renderHeading("Convoy"));
trackedRange("Vehicles", "vehicles", 2, 8, 1);
trackedRange("Spacing", "spacing", 25, 150, 5, (v) => `${v} m`);
trackedRange("Speed", "speed", 15, 70, 5, (v) => `${v} km/h`);

controlsEl.appendChild(renderHeading("Hard-kill (per vehicle)"));
trackedRange("Engagement range", "range", 100, 500, 10, (v) => `${v} m`);
trackedRange("Reaction time", "react", 0.4, 3, 0.1, (v) => `${v.toFixed(1)} s`);
trackedRange("Pk per shot", "pk", 0.3, 0.95, 0.05, (v) => v.toFixed(2));
trackedRange("Magazine", "mag", 2, 16, 1, (v) => `${v} rds`);
trackedRange("Fire channels", "channels", 1, 2, 1);
trackedRange("Sensor range (open)", "sensor", 400, 2000, 50, (v) => `${v} m`);

controlsEl.appendChild(renderHeading("Terrain"));
trackedRange("Forest standoff", "standoff", 40, 400, 10, (v) => `${v} m`);
trackedRange("Forest density", "density", 0.2, 1, 0.1, (v) => v.toFixed(1));
trackedRange("Canopy height", "canopy", 10, 30, 1, (v) => `${v} m`);

controlsEl.appendChild(renderHeading("Attack"));
trackedRange("Drones", "drones", 1, 80, 1);
trackedRange("Drone speed", "dspeed", 15, 80, 5, (v) => `${v} m/s`);
trackedRange("Ambush events", "events", 1, 3, 1);
trackedSelect<"1" | "2", "axes">("Launch axes", "axes", [
  ["1", "Single axis"],
  ["2", "Two-axis pincer"],
], (v) => (v === "2" ? 2 : 1));
trackedSelect<ConvoyTargeting, "targeting">("Targeting", "targeting", [
  ["concentrate", "Concentrate on lead"],
  ["spread", "Spread across convoy"],
]);

controlsEl.appendChild(renderHeading("Top cover"));
trackedSelect<ConvoyTopCover, "topCover">("Layer", "topCover", [
  ["none", "None — organic sensors only"],
  ["mast", "Telescopic sensor mast"],
  ["uav", "UAV overwatch (sensor only)"],
  ["armed", "Armed overwatch"],
]);

trackedRange("Seed", "seed", 1, 99, 1);

function applyPreset(p: (typeof CONVOY_PRESETS)[number]): void {
  isApplyingPreset = true;
  for (const [k, v] of Object.entries(p.config)) {
    (state as unknown as Record<string, unknown>)[k] = v;
    const setter = setterByKey.get(k);
    if (setter) setter(v as never);
  }
  presetDesc.textContent = p.description;
  isApplyingPreset = false;
}
presetSelect.addEventListener("change", () => {
  const preset = CONVOY_PRESETS.find((p) => p.id === presetSelect.value);
  if (preset) {
    tracker.event("preset_change", { event_label: preset.id });
    applyPreset(preset);
    runAndShow();
  }
});
const initialPreset = CONVOY_PRESETS[0];
if (initialPreset) {
  applyPreset(initialPreset);
  presetSelect.value = initialPreset.id;
}

const btnRow = document.createElement("div");
btnRow.className = "btn-row";
const runBtn = document.createElement("button");
runBtn.textContent = "Run simulation";
runBtn.addEventListener("click", () => {
  runAndShow();
  startPlayback();
});
const replayBtn = document.createElement("button");
replayBtn.textContent = "Replay";
replayBtn.className = "secondary";
replayBtn.disabled = true;
replayBtn.addEventListener("click", () => {
  tracker.event("replay");
  startPlayback();
});
btnRow.append(runBtn, replayBtn);
controlsEl.appendChild(btnRow);

controlsEl.appendChild(renderHeading("Display"));
const themeRow = document.createElement("div");
themeRow.className = "speed-row";
const themeButtons = new Map<ThemeName, HTMLButtonElement>();
for (const t of ["light", "dark"] as const) {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "pill";
  b.textContent = t === "light" ? "Map" : "Tactical";
  if (t === DEFAULT_THEME) b.classList.add("is-active");
  b.addEventListener("click", () => {
    theme = t;
    for (const [k, el] of themeButtons) el.classList.toggle("is-active", k === t);
    document.documentElement.dataset.theme = t;
    renderer.setTheme(t);
    paintLegendIcons();
    tracker.event("theme_change", { event_label: t === "light" ? "map" : "tactical" });
    if (!playing) renderer.drawFrame(simT);
  });
  themeButtons.set(t, b);
  themeRow.appendChild(b);
}
controlsEl.appendChild(themeRow);
document.documentElement.dataset.theme = DEFAULT_THEME;

let deadGroundOn = true;
const dgBtn = document.createElement("button");
dgBtn.type = "button";
dgBtn.className = "toggle-btn is-active";
dgBtn.textContent = "Dead ground: on";
dgBtn.addEventListener("click", () => {
  deadGroundOn = !deadGroundOn;
  dgBtn.classList.toggle("is-active", deadGroundOn);
  dgBtn.textContent = deadGroundOn ? "Dead ground: on" : "Dead ground: off";
  renderer.setDeadGround(deadGroundOn);
  tracker.event("dead_ground_toggle", { event_label: deadGroundOn ? "on" : "off" });
  if (!playing) renderer.drawFrame(simT);
});
controlsEl.appendChild(dgBtn);

controlsEl.appendChild(renderHeading("Playback"));
const speedRow = document.createElement("div");
speedRow.className = "speed-row";
const speedButtons = new Map<number, HTMLButtonElement>();
for (const s of SPEED_OPTIONS) {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "pill";
  b.textContent = `${s}×`;
  if (s === DEFAULT_SPEED) b.classList.add("is-active");
  b.addEventListener("click", () => {
    playbackSpeed = s;
    for (const [v, el] of speedButtons) el.classList.toggle("is-active", v === s);
  });
  speedButtons.set(s, b);
  speedRow.appendChild(b);
}
controlsEl.appendChild(speedRow);

// Legend icons are painted with the same sprite code and palette as the map,
// so what the key shows is exactly what the canvas shows.
type LegendKind = "vehicle" | "drone" | "overwatch" | "forest" | "dead" | "owhidden";
const LEGEND_ROWS: Array<[LegendKind, string]> = [
  ["vehicle", "Convoy vehicle"],
  ["drone", "FPV attack drone"],
  ["overwatch", "Overwatch platform (orbiting)"],
  ["forest", "Forest — canopy blocks line of sight"],
  ["dead", "Dead ground (hidden from convoy)"],
  ["owhidden", "Hidden from overwatch"],
];
const legend = document.createElement("div");
legend.className = "legend convoy-legend";
const legendCanvases = new Map<LegendKind, HTMLCanvasElement>();
for (const [kind, label] of LEGEND_ROWS) {
  const row = document.createElement("div");
  row.className = "legend-row static";
  const icon = document.createElement("canvas");
  icon.className = "legend-icon";
  icon.setAttribute("aria-hidden", "true");
  const text = document.createElement("span");
  text.className = "legend-label";
  text.textContent = label;
  row.append(icon, text);
  legendCanvases.set(kind, icon);
  legend.appendChild(row);
}
controlsEl.appendChild(legend);

function paintLegendIcons(): void {
  const pal = getConvoyPalette(theme);
  const cssSize = 24;
  const dpr = window.devicePixelRatio || 1;
  for (const [kind, icon] of legendCanvases) {
    icon.width = Math.floor(cssSize * dpr);
    icon.height = Math.floor(cssSize * dpr);
    icon.style.width = `${cssSize}px`;
    icon.style.height = `${cssSize}px`;
    const g = icon.getContext("2d");
    if (!g) continue;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, cssSize, cssSize);
    const c = cssSize / 2;
    switch (kind) {
      case "vehicle":
        g.fillStyle = pal.vehicle;
        g.beginPath();
        g.roundRect(c - 9, c - 5.5, 18, 11, 3);
        g.fill();
        g.fillStyle = pal.vehicleLabel;
        g.font = "bold 7px ui-monospace, monospace";
        g.textAlign = "center";
        g.textBaseline = "middle";
        g.fillText("V1", c, c + 0.5);
        break;
      case "drone":
        g.save();
        g.translate(c, c);
        g.rotate(-Math.PI / 8);
        drawDroneSprite(g, pal.drone, 1.5);
        g.restore();
        break;
      case "overwatch":
        g.strokeStyle = pal.overwatchRing;
        g.setLineDash([3, 3]);
        g.beginPath();
        g.arc(c, c, 9.5, 0, Math.PI * 2);
        g.stroke();
        g.setLineDash([]);
        g.fillStyle = pal.overwatch;
        g.strokeStyle = pal.overwatch;
        g.beginPath();
        g.arc(c, c, 3, 0, Math.PI * 2);
        g.fill();
        g.lineWidth = 1.4;
        g.beginPath();
        g.moveTo(c - 6, c);
        g.lineTo(c + 6, c);
        g.moveTo(c, c - 6);
        g.lineTo(c, c + 6);
        g.stroke();
        break;
      case "forest": {
        g.fillStyle = pal.forest;
        g.strokeStyle = pal.forestEdge;
        g.lineWidth = 1.2;
        g.beginPath();
        g.ellipse(c, c, 10, 8, 0.3, 0, Math.PI * 2);
        g.fill();
        g.stroke();
        g.fillStyle = pal.forestSpeckle;
        for (const [dx, dy] of [[-4, -2], [3, -3], [0, 3], [5, 2]] as const) {
          g.beginPath();
          g.arc(c + dx, c + dy, 1.4, 0, Math.PI * 2);
          g.fill();
        }
        break;
      }
      case "dead":
        g.globalAlpha = Math.min(1, pal.deadGroundAlpha * 2.2);
        g.fillStyle = pal.deadGround;
        g.fillRect(c - 10, c - 7, 20, 14);
        g.globalAlpha = 1;
        break;
      case "owhidden":
        g.globalAlpha = Math.min(1, pal.owHiddenAlpha * 2.2);
        g.fillStyle = pal.owHidden;
        g.fillRect(c - 10, c - 7, 20, 14);
        g.globalAlpha = 1;
        break;
    }
  }
}
paintLegendIcons();

/* ---------- toolbar ---------- */

toolbarEl.innerHTML = "";
const playBtn = document.createElement("button");
playBtn.type = "button";
playBtn.className = "toolbar-icon-btn";
playBtn.title = "Play / pause (Space)";
playBtn.setAttribute("aria-label", "Play / pause");
playBtn.innerHTML = playGlyph(false);
playBtn.addEventListener("click", () => togglePlay());
toolbarEl.appendChild(playBtn);

const contactBtn = document.createElement("button");
contactBtn.type = "button";
contactBtn.className = "toolbar-export-btn toolbar-contact-btn";
contactBtn.innerHTML = `${ffGlyph()}<span>Contact</span>`;
contactBtn.title = "Jump to first contact";
contactBtn.disabled = true;
contactBtn.addEventListener("click", () => {
  if (!result || result.summary.firstContact === null) return;
  tracker.event("contact_jump");
  seekTo(Math.max(0, result.summary.firstContact - 2));
});
toolbarEl.appendChild(contactBtn);

const timeline = document.createElement("input");
timeline.type = "range";
timeline.className = "timeline";
timeline.min = "0";
timeline.max = "10";
timeline.step = "0.05";
timeline.value = "0";
timeline.disabled = true;
timeline.addEventListener("input", () => {
  playing = false;
  playBtn.innerHTML = playGlyph(false);
  seekTo(Number.parseFloat(timeline.value));
});
toolbarEl.appendChild(timeline);

const timeReadout = document.createElement("span");
timeReadout.className = "timeline-readout";
timeReadout.textContent = "0.0s";
toolbarEl.appendChild(timeReadout);

const exportBtn = document.createElement("button");
exportBtn.type = "button";
exportBtn.className = "toolbar-export-btn";
exportBtn.textContent = "Export PNG";
exportBtn.disabled = true;
exportBtn.addEventListener("click", () => {
  if (!result || exportInProgress) return;
  tracker.event("export_png", { event_label: `seed_${result.config.seed}` });
  tracker.shareEvent("export", `seed=${result.config.seed} t=${simT.toFixed(1)}`);
  exportInProgress = true;
  const off = document.createElement("canvas");
  const offRenderer = makeConvoyRenderer(off);
  const size = renderer.getLogicalSize();
  offRenderer.configure(size.width, size.height, 2);
  offRenderer.setTheme(theme);
  offRenderer.setDeadGround(deadGroundOn);
  offRenderer.setResult(result);
  offRenderer.drawFrame(simT);
  off.toBlob((blob) => {
    if (blob) {
      downloadBlob(blob, `convoy-sim-seed${result?.config.seed}-t${simT.toFixed(1).replace(".", "_")}s.png`);
    }
    exportInProgress = false;
  }, "image/png");
});
toolbarEl.appendChild(exportBtn);

const shareBtn = document.createElement("button");
shareBtn.type = "button";
shareBtn.className = "toolbar-export-btn";
shareBtn.textContent = "Share setup";
shareBtn.title = "Copy a link that reproduces this exact configuration";
shareBtn.addEventListener("click", () => {
  const query = encodeConfigQuery(state as unknown as Record<string, unknown>, defaultConvoyConfig() as unknown as Record<string, unknown>);
  const url = `${window.location.origin}${window.location.pathname}${query ? `?${query}` : ""}`;
  void copyText(url).then((ok) => {
    tracker.event("share_copy", { event_label: presetSelect.value });
    shareBtn.textContent = ok ? "Copied ✓" : url;
    setTimeout(() => {
      shareBtn.textContent = "Share setup";
    }, 1600);
  });
});
toolbarEl.appendChild(shareBtn);

/* ---------- scorecard ---------- */

scorecardEl.innerHTML = "";
scorecardEl.appendChild(renderHeading("Scorecard"));
const scoreList = document.createElement("ul");
scorecardEl.appendChild(scoreList);

function updateScorecard(s: ConvoySummary | null): void {
  scoreList.innerHTML = "";
  if (!s) return;
  const pct = (x: number): string => `${Math.round(x * 100)}%`;
  const items: Array<[string, string, "good" | "bad" | ""]> = [
    ["Vehicles intact", `${s.intactPct}%`, s.intactPct === 100 ? "good" : s.intactPct < 60 ? "bad" : ""],
    ["Vehicles lost", `${s.lost}`, s.lost ? "bad" : "good"],
    ["Drones launched", `${s.launched}`, ""],
    ["Killed by convoy", `${s.killedV}`, ""],
    ["Killed by overwatch", `${s.killedO}`, ""],
    ["Leakers", `${s.leakers}`, s.leakers ? "bad" : "good"],
    ["Mean warning", `${s.warnMean.toFixed(1)}s`, ""],
    ["Worst warning", `${s.warnMin.toFixed(1)}s`, ""],
    ["Saturation events", `${s.sat}`, s.sat ? "bad" : ""],
    ["Shots fired", s.owShots ? `${s.shots} +${s.owShots} ow` : `${s.shots}`, ""],
    ["Realised Pk", pct(s.realPk), ""],
    ["Rounds remaining", `${s.ammoLeft}`, ""],
  ];
  for (const [k, v, cls] of items) {
    const li = document.createElement("li");
    li.innerHTML = `<span>${k}</span><span class="v ${cls}">${v}</span>`;
    scoreList.appendChild(li);
  }
}

/* ---------- playback ---------- */

function ffGlyph(): string {
  return `<svg width="11" height="11" viewBox="0 0 14 14" aria-hidden="true">
    <polygon points="1,2 7,7 1,12" fill="currentColor"/>
    <polygon points="7,2 13,7 7,12" fill="currentColor"/>
  </svg>`;
}

function playGlyph(isPlaying: boolean): string {
  if (isPlaying) {
    return `<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <rect x="2" y="2" width="3.5" height="10" fill="currentColor"/>
      <rect x="8.5" y="2" width="3.5" height="10" fill="currentColor"/>
    </svg>`;
  }
  return `<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <polygon points="3,2 12,7 3,12" fill="currentColor"/>
  </svg>`;
}

function endTime(): number {
  if (!result) return 0;
  const last = result.frames[result.frames.length - 1];
  return last ? last.t : 0;
}

function setStatus(s: string): void {
  overlay.textContent = s;
}

function seekTo(t: number): void {
  simT = Math.max(0, Math.min(endTime(), t));
  timeline.value = `${simT}`;
  timeReadout.textContent = `${simT.toFixed(1)}s`;
  if (!playing) setStatus(`Paused · t=${simT.toFixed(1)}s`);
  renderer.drawFrame(simT);
}

function togglePlay(): void {
  if (!result) return;
  if (playing) {
    playing = false;
    playBtn.innerHTML = playGlyph(false);
    setStatus(`Paused · t=${simT.toFixed(1)}s`);
  } else {
    if (simT >= endTime()) simT = 0;
    playing = true;
    playBtn.innerHTML = playGlyph(true);
    lastFrame = performance.now();
    requestAnimationFrame(tick);
  }
}

function startPlayback(): void {
  if (!result) return;
  simT = 0;
  playing = true;
  playBtn.innerHTML = playGlyph(true);
  lastFrame = performance.now();
  requestAnimationFrame(tick);
}

function tick(now: number): void {
  if (!result) return;
  const realDt = (now - lastFrame) / 1000;
  lastFrame = now;
  if (playing) {
    simT += realDt * playbackSpeed;
    const end = endTime();
    if (simT >= end) {
      simT = end;
      playing = false;
      playBtn.innerHTML = playGlyph(false);
      setStatus(`Done · t=${simT.toFixed(1)}s`);
    } else {
      setStatus(`t=${simT.toFixed(1)}s · ${playbackSpeed}× speed`);
    }
    timeline.value = `${simT}`;
    timeReadout.textContent = `${simT.toFixed(1)}s`;
  }
  renderer.drawFrame(simT);
  if (playing) requestAnimationFrame(tick);
}

document.addEventListener("keydown", (e) => {
  if (e.code !== "Space") return;
  const tag = document.activeElement?.tagName;
  if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA" || tag === "BUTTON") return;
  e.preventDefault();
  togglePlay();
});

/* ---------- run ---------- */

function runAndShow(auto = false): void {
  setStatus("Running…");
  const cfg = { ...state };
  if (!auto) {
    tracker.event("run", { event_label: presetSelect.value, value: cfg.seed });
    tracker.shareEvent("run", `${presetSelect.value} seed=${cfg.seed} drones=${cfg.drones} cover=${cfg.topCover}`);
  }
  result = runConvoySim(cfg);
  counterfactual =
    cfg.topCover === "armed" ? null : runConvoySim({ ...cfg, topCover: "armed" }, { light: true }).summary;
  renderer.setResult(result);
  updateScorecard(result.summary);
  analysisEl.innerHTML = buildRunAnalysis(cfg, result.summary);
  cmpEl.innerHTML = buildCounterfactual(result.summary, counterfactual);
  replayBtn.disabled = false;
  exportBtn.disabled = false;
  contactBtn.disabled = result.summary.firstContact === null;
  timeline.disabled = false;
  timeline.max = `${Math.max(0.1, endTime()).toFixed(2)}`;
  playing = false;
  playBtn.innerHTML = playGlyph(false);
  const fc = result.summary.firstContact;
  seekTo(fc !== null ? Math.max(0, fc - 2) : 0);
  setStatus(`Paused · t=${simT.toFixed(1)}s — press play`);
}

window.addEventListener("resize", () => {
  renderer.resize();
  renderer.drawFrame(simT);
});

/* ---------- config from URL (Share button / tracked links) ---------- */

const NUM_LIMITS: Partial<Record<keyof ConvoyConfig, [number, number]>> = {
  vehicles: [2, 8],
  spacing: [25, 150],
  speed: [15, 70],
  range: [100, 500],
  react: [0.4, 3],
  pk: [0.3, 0.95],
  mag: [2, 16],
  channels: [1, 2],
  sensor: [400, 2000],
  standoff: [40, 400],
  density: [0.2, 1],
  canopy: [10, 30],
  drones: [1, 80],
  dspeed: [15, 80],
  events: [1, 3],
  seed: [1, 99],
};
const TOP_COVERS: ConvoyTopCover[] = ["none", "mast", "uav", "armed"];
const TARGETINGS: ConvoyTargeting[] = ["concentrate", "spread"];

function applyUrlConfig(): boolean {
  const raw = parseConfigQuery(defaultConvoyConfig() as unknown as Record<string, unknown>);
  const cfg: Partial<ConvoyConfig> = {};
  for (const [k, v] of Object.entries(raw)) {
    const key = k as keyof ConvoyConfig;
    const limits = NUM_LIMITS[key];
    if (limits && typeof v === "number") {
      (cfg as Record<string, unknown>)[key] = Math.min(limits[1], Math.max(limits[0], v));
    } else if (key === "axes" && typeof v === "number") {
      cfg.axes = v === 2 ? 2 : 1;
    } else if (key === "topCover" && TOP_COVERS.includes(v as ConvoyTopCover)) {
      cfg.topCover = v as ConvoyTopCover;
    } else if (key === "targeting" && TARGETINGS.includes(v as ConvoyTargeting)) {
      cfg.targeting = v as ConvoyTargeting;
    }
  }
  const keys = Object.keys(cfg);
  if (keys.length === 0) return false;
  isApplyingPreset = true;
  for (const [k, v] of Object.entries(cfg)) {
    (state as unknown as Record<string, unknown>)[k] = v;
    const setter = setterByKey.get(k);
    if (setter) setter(v as never);
  }
  isApplyingPreset = false;
  presetSelect.value = "__custom__";
  presetDesc.textContent = "Shared configuration (from link).";
  return true;
}

renderer.setTheme(theme);
renderer.resize();
applyUrlConfig();
runAndShow(true);
